const m = require('mithril');
const prop = require('mithril/stream');
const EventEmitter = require('events');
// Leaflet styles and plugins
const L = require('leaflet');
let mapbox = null;
let mapboxgl = null;
try {
  mapboxgl = require('mapbox-gl');
  mapbox = require('mapbox-gl-leaflet'); // important
} catch (error) {
  console.warn('[map] mapbox-gl-leaflet failed to load, continuing without Mapbox tiles.', error);
}
const style = require('leaflet/dist/leaflet.css'); // important

L.Marker.prototype.options.icon.options.imagePath = '/images/';

const { getLocationCoordinates, CONTEXTS, LOCATION_CLASSES, getFeatureUid, parseFeatureUid, isRoomClass } = require('./mapUtils');
const { LOCATION_CONFIGS } = require('./locationConfigs');
const DEFAULT_ZOOM_LEVELS = { 'Site': 15, 'Building': 18, 'Level': 20, 'Room': 21 };
const MAPBOX_STYLE_ID = 'lukelaws/clqwqv9ry00pd01qx0hq3eiui';
const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1IjoibHVrZWxhd3MiLCJhIjoiY2xxd2h5MTV6MDM1aTJrbzI5a3d0MjBkZSJ9.R24NEny15crfdXI24FOCSg';
const MAPBOX_RASTER_SCALE = '@2x';
const MAPBOX_RASTER_TILE_URL = `https://api.mapbox.com/styles/v1/${MAPBOX_STYLE_ID}/tiles/512/{z}/{x}/{y}${MAPBOX_RASTER_SCALE}?access_token=${MAPBOX_ACCESS_TOKEN}`;

// Plugin registration order matters for Leaflet control stacking when multiple
// controls share the same corner. Ensure navOverlay (path header) is added first
// so it appears at the top of the 'topleft' stack above edit controls.
const PLUGINS = [
  require('./modules/navOverlay'),
  require('./modules/fullscreen'),
  require('./modules/editMode'),
  require('./modules/placeBuilding'),
  require('./modules/markerClusters'),
  require('./modules/diagramOverlay'),
  require('./modules/locationLayers'),
  require('./modules/zoneLayers'),
  require('./modules/equipmentLayers')
];

const EVENT_BUS = new EventEmitter();
const FALLBACK_COORDINATES = {latitude: 25.789845, longitude: -80.211021};

function supportsWebGL() {
  if (typeof document === 'undefined') return false;
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl || typeof gl.getParameter !== 'function') return false;
    if (typeof gl.isContextLost === 'function' && gl.isContextLost()) return false;

    const pointSizeRange = gl.getParameter(gl.ALIASED_POINT_SIZE_RANGE);
    return Boolean(
      pointSizeRange &&
      pointSizeRange.length >= 2 &&
      Number.isFinite(Number(pointSizeRange[1])) &&
      Number(pointSizeRange[1]) > 0
    );
  } catch (error) {
    return false;
  }
}

function shouldDisableMapbox() {
  if (typeof window === 'undefined') return true;
  if (window.__NR_DISABLE_MAPBOX__ || window.NR_DISABLE_MAPBOX) return true;
  return false;
}

function shouldDisableMapboxGL() {
  if (typeof window === 'undefined') return true;
  return Boolean(
    window.__NR_DISABLE_MAPBOX_GL__ ||
    window.NR_DISABLE_MAPBOX_GL ||
    window.__NR_FORCE_MAPBOX_RASTER__ ||
    window.NR_FORCE_MAPBOX_RASTER
  );
}

function mapboxGLRuntimeSupported() {
  if (!supportsWebGL()) return false;
  if (mapboxgl && typeof mapboxgl.supported === 'function') {
    try {
      return Boolean(mapboxgl.supported({ failIfMajorPerformanceCaveat: true }));
    } catch (error) {
      return false;
    }
  }
  return true;
}

function shouldUseMapboxGL() {
  if (typeof window === 'undefined') return false;
  if (shouldDisableMapbox()) return false;
  if (shouldDisableMapboxGL()) return false;
  return mapboxGLRuntimeSupported();
}

function shouldForceSvgRenderer() {
  if (typeof window === 'undefined') return false;
  return Boolean(window.__NR_FORCE_SVG__ || window.NR_FORCE_SVG);
}

function createMapboxRasterBaseLayer() {
  if (shouldDisableMapbox()) return null;
  try {
    return L.tileLayer(MAPBOX_RASTER_TILE_URL, {
      attribution: '© Mapbox © OpenStreetMap',
      tileSize: 512,
      zoomOffset: -1,
      maxNativeZoom: 22,
      maxZoom: 28,
    });
  } catch (error) {
    console.warn('[map] mapbox raster tiles failed to initialize, falling back to grid tiles.', error);
    return null;
  }
}

function createFallbackBaseLayer() {
  if (typeof document === 'undefined') return null;
  return L.gridLayer({
    attribution: '',
    tileSize: 256,
    createTile: () => {
      const tile = document.createElement('div');
      tile.style.setProperty('background-color', '#f0f0f0', 'important');
      tile.style.setProperty('border', '1px solid #d0d0d0', 'important');
      tile.style.width = '256px';
      tile.style.height = '256px';
      return tile;
    }
  });
}

function addRasterFallback(ctrl, glLayer, reason) {
  if (!ctrl || !ctrl.map) return;
  if (glLayer && ctrl.map.hasLayer(glLayer)) {
    ctrl.map.removeLayer(glLayer);
  }
  const rasterLayer = createMapboxRasterBaseLayer() || createFallbackBaseLayer();
  if (rasterLayer) {
    rasterLayer.addTo(ctrl.map);
  }
  if (reason) {
    console.warn('[map] mapbox-gl failed after initialization, using raster tiles.', reason);
  }
}

function watchMapboxGL(ctrl, glLayer) {
  const glMap = glLayer && typeof glLayer.getMapboxMap === 'function'
    ? glLayer.getMapboxMap()
    : null;

  if (!glMap || typeof glMap.on !== 'function') {
    addRasterFallback(ctrl, glLayer, 'mapbox-gl map unavailable');
    return;
  }

  let fallbackApplied = false;
  const markLoaded = () => {
    if (ctrl._mapboxGLFallbackTimer) {
      clearTimeout(ctrl._mapboxGLFallbackTimer);
      ctrl._mapboxGLFallbackTimer = null;
    }
  };
  const fallback = (event) => {
    if (fallbackApplied) return;
    fallbackApplied = true;
    if (ctrl._mapboxGLFallbackTimer) {
      clearTimeout(ctrl._mapboxGLFallbackTimer);
      ctrl._mapboxGLFallbackTimer = null;
    }
    addRasterFallback(ctrl, glLayer, event && event.error ? event.error : event);
  };

  if (typeof glMap.once === 'function') {
    glMap.once('load', markLoaded);
  } else {
    glMap.on('load', markLoaded);
  }
  glMap.on('error', fallback);
  glMap.on('webglcontextlost', fallback);

  ctrl._mapboxGLFallbackTimer = setTimeout(() => {
    fallback('mapbox-gl load timeout');
  }, 5000);
}

function oninit (vnode) {
  let ctrl = this;
  ctrl.contexts = CONTEXTS;
  ctrl.eventBus = EVENT_BUS;
  ctrl.eventBus.removeAllListeners();
  ctrl.eventBus.emit('mithril:beforeinit');
  console.log(vnode.attrs);
  // Persist fullscreen callback from attrs
  ctrl.onFullscreenChangeCb = typeof vnode.attrs.onFullscreenChange === 'function'
    ? vnode.attrs.onFullscreenChange
    : null;

  ctrl.location = vnode.attrs.location;
  ctrl.ancestors = () => ctrl.location().path;
  ctrl.childClass = LOCATION_CLASSES[LOCATION_CLASSES.indexOf(ctrl.location()['@class']) + 1] ?? 'Room';
  ctrl.mapSelectedView = vnode.attrs.mapSelectedView || prop('Overview');
  ctrl.lastLocation = vnode.attrs.location;
  ctrl.locationConfig = vnode.attrs.locationConfig || (() => {
    const locationClass = ctrl.location()?.['@class'];
    return isRoomClass(locationClass) ? LOCATION_CONFIGS.Room : LOCATION_CONFIGS[locationClass];
  });
  ctrl.newFeatureStyle = vnode.attrs.newFeatureStyle;
  // Option: show path overlay even when not in fullscreen
  ctrl.showPathOverlay = Boolean(vnode.attrs.showPathOverlay);
  ctrl._originalBodyOverflow = null;
  // Record Groups
  ctrl.allLayers = [];
  ctrl.thisFeatures = vnode.attrs.thisFeatures ? vnode.attrs.thisFeatures : () => [ctrl.location()];
  ctrl.grandparentLocation = vnode.attrs.grandparentLocation;
  ctrl.grandparentFeatures = () => typeof ctrl.grandparentLocation == 'function' ? [ctrl.grandparentLocation()] : [];
  ctrl.parentLocation = vnode.attrs.parentLocation;
  ctrl.lastParentLocation = vnode.attrs.lastParentLocation;
  ctrl.parentFeatures = () => typeof ctrl.parentLocation == 'function' ? [ctrl.parentLocation()] : [];
  ctrl.childFeatures = vnode.attrs.childFeatures || prop([]);
  ctrl.siblingFeatures = vnode.attrs.siblingFeatures || prop([]);
  ctrl.grandchildFeatures = vnode.attrs.grandchildFeatures || prop([]);
  // Equipment and System Pages
  ctrl.equipmentRecord = vnode.attrs.equipmentRecord;
  ctrl.systemRecord = vnode.attrs.systemRecord;
  // Parent component references
  ctrl.parentReinitialize = vnode.attrs.parentReinitialize;
  ctrl.parentReinitializeAndRedraw = vnode.attrs.parentReinitializeAndRedraw;
  ctrl.focusRid = vnode.attrs.focusRid; // Optional: center/fit to this entity if present
  // Optional: additional rids to include in focus bounds (e.g., component children)
  ctrl.focusRidChildren = vnode.attrs.focusRidChildren || [];
  // Optional: rids to always render regardless of selected view/category filters
  ctrl.alwaysIncludeRids = vnode.attrs.alwaysIncludeRids || [];

  // Handle missing location coordinates
  try {
    ctrl.thisLocationCoordinates = getLocationCoordinates(ctrl.location());
    if (!ctrl.thisLocationCoordinates.latitude) {
      if (ctrl?.parentLocation && ctrl.parentLocation()) {
        let parentCoordinates = getLocationCoordinates(ctrl.parentLocation());
        if (parentCoordinates.latitude) {
          ctrl.thisLocationCoordinates = parentCoordinates;
        }
      } else {
        ctrl.thisLocationCoordinates = FALLBACK_COORDINATES;
      } 
    }
  } catch (e) {
    console.error('Error getting location coordinates for this location', e)
    ctrl.thisLocationCoordinates = FALLBACK_COORDINATES;
  }
  
  ctrl.redrawDiagram = vnode.attrs.redrawDiagram;
  ctrl.getFeatureUid = getFeatureUid;
  ctrl.parseFeatureUid = parseFeatureUid;
  // Internal focus retry state (handles async layer population)
  ctrl._focusRetryCount = 0;
  ctrl._maxFocusRetries = 15; // ~1.5s total with 100ms delay
  ctrl._focusRetryDelayMs = 100;
  ctrl._viewportRetryCount = 0;
  ctrl._maxViewportRetries = 10;
  ctrl._viewportRetryDelayMs = 200;
  ctrl._mapResizeObserver = null;
  ctrl._mapResizeFrame = null;
  ctrl.scheduleMapResize = () => {
    if (ctrl._mapResizeFrame || typeof window === 'undefined') return;
    ctrl._mapResizeFrame = window.requestAnimationFrame(() => {
      ctrl._mapResizeFrame = null;
      if (ctrl?.map) ctrl.map.invalidateSize();
    });
  };
  
  ctrl.getActiveContexts = () => {
    return ctrl.contexts.filter(context => {
      return ctrl?.locationConfig()?.markers?.includes(context.name) || ctrl.locationConfig()?.polygons?.includes(context.name)
    });
  };

  ctrl.getCurrentFeatures = () => {
    let features = [];
    ctrl.getActiveContexts().forEach(context => {
      ctrl[context.name + 'Features']().forEach(feature => {
        if (!feature || Object.keys(feature).length == 0) return;
        if(!feature.context) feature.context = context.name;
        if (!feature.UID) feature.UID = ctrl.getFeatureUid(feature);
        features.push(feature);
      });
    });
    return features;
  };

  ctrl.addContextFeatures = (context) => {
    let contextRecord = typeof context == 'string' ? ctrl.contexts.find(c => c.name == context) : context;
    let features = ctrl[contextRecord.name + 'Features']();
    ctrl.eventBus.emit('featuresToAdd', features);
  };

  ctrl.removeContextFeatures = (context) => {
    let contextRecord = typeof context == 'string' ? ctrl.contexts.find(c => c.name == context) : context;
    let features = ctrl[contextRecord.name + 'Features']();
    ctrl.eventBus.emit('featuresToRemove', features.map(ctrl.getFeatureUid));
  };

  ctrl.getFeaturesForContext = (context) => {
    let contextRecord = typeof context == 'string' ? ctrl.contexts.find(c => c.name == context) : context;
    return ctrl[contextRecord.name + 'Features']();
  };

  ctrl.getLayersForContext = (contextName) => {
    return ctrl.allLayers.filter(layer => layer.context == contextName);
  };

  ctrl.setViewportAndAdjustBounds = function (options = {}) {
    const resetViewportRetries = () => { ctrl._viewportRetryCount = 0; };
    const scheduleViewportRetry = () => {
      if (ctrl._viewportRetryCount >= ctrl._maxViewportRetries) return false;
      ctrl._viewportRetryCount += 1;
      setTimeout(() => {
        if (ctrl?.map) ctrl.setViewportAndAdjustBounds(options);
      }, ctrl._viewportRetryDelayMs);
      return true;
    };

    const deferFallback = Boolean(options.deferFallback);
    const focus = ctrl.locationConfig()?.viewport || 'this';
    const featuresInViewport = focus === 'all' ? ctrl.allLayers : ctrl.getLayersForContext(focus);
    
    const setView = (coordinates, zoomLevel) => {
      ctrl.map.setView([coordinates.latitude, coordinates.longitude], zoomLevel);
    };

    const setFallbackView = () => {
      const coordinates = ctrl.thisLocationCoordinates.latitude ? ctrl.thisLocationCoordinates : FALLBACK_COORDINATES;
      const currentLocationClass = ctrl.location()?.['@class'];
      const zoomLevel = isRoomClass(currentLocationClass)
        ? (DEFAULT_ZOOM_LEVELS.Room ?? 15)
        : (DEFAULT_ZOOM_LEVELS[currentLocationClass] ?? 15);
      setView(coordinates, zoomLevel);
    };

    // Compute bounds while ignoring Bounding_Box helper features for tight fits
    const computeTightBounds = (layers) => {
      let union = null;
      const addBounds = (b) => {
        if (b && b.isValid()) {
          union = union ? union.extend(b) : b;
        }
      };
      layers.forEach(layer => {
        if (!layer) return;
        if (typeof layer.getLayers === 'function') {
          const childLayers = layer.getLayers();
          childLayers.forEach(cl => {
            const name = cl?.feature?.properties?.name;
            if (name === 'Bounding_Box') return;
            if (typeof cl.getBounds === 'function') addBounds(cl.getBounds());
          });
        } else if (layer?.feature?.properties?.name === 'Bounding_Box') {
          return;
        } else if (typeof layer.getBounds === 'function') {
          addBounds(layer.getBounds());
        }
      });
      return union;
    };

    // If a focusRid was provided, try to fit the map to that feature first
    if (ctrl.focusRid) {
      try {
        const childRids = Array.isArray(ctrl.focusRidChildren)
          ? ctrl.focusRidChildren
          : (typeof ctrl.focusRidChildren === 'function' ? ctrl.focusRidChildren() : []);
        const focusRidSet = new Set([ctrl.focusRid, ...childRids].filter(Boolean));
        const focusLayers = ctrl.allLayers.filter(l => {
          if (!l || !l.UID) return false;
          const parsed = ctrl.parseFeatureUid(l.UID);
          return parsed && focusRidSet.has(parsed.rid);
        });

        if (focusLayers.length > 0) {
          const cls = ctrl.location()?.['@class'];
          const isLevel = cls === 'Level';
          const isRoom = isRoomClass(cls);
          const bounds = (isLevel || isRoom)
            ? computeTightBounds(focusLayers)
            : L.featureGroup(focusLayers).getBounds();
          if (bounds && bounds.isValid()) {
            const padding = isLevel ? [1, 1] : (isRoom ? [8, 8] : [40, 40]);
            ctrl.map.flyToBounds(bounds, { padding, duration: 0.2 });
            // After the fly animation completes, adjust zoom for context
            ctrl.map.once('moveend', () => {
              if (cls === 'Site' || cls === 'Building') {
                ctrl.map.setZoom(ctrl.map.getZoom() - 1);
              } else if (isLevel) {
                ctrl.map.setZoom(ctrl.map.getZoom() + 0.5);
              } else if (isRoom) {
                ctrl.map.setZoom(ctrl.map.getZoom() - 0.25);
              }
            });
            ctrl._focusRetryCount = 0; // reset retries on success
            resetViewportRetries();
            return; // Focus handled
          }
        } else if (ctrl._focusRetryCount < ctrl._maxFocusRetries) {
          // Layers may not be ready yet (plugins add asynchronously). Retry shortly.
          ctrl._focusRetryCount += 1;
          setTimeout(() => {
            // Only retry if component is still alive and map exists
            if (ctrl?.map) ctrl.setViewportAndAdjustBounds();
          }, ctrl._focusRetryDelayMs);
          return; // Defer default viewport until retries exhausted
        }
      } catch (e) {
        console.error('Error centering on focusRid', ctrl.focusRid, e);
      }
    }

    if (featuresInViewport.length > 0) {
      const cls = ctrl.location()?.['@class'];
      const isLevel = cls === 'Level';
      const isRoom = isRoomClass(cls);
      const bounds = (isLevel || isRoom)
        ? computeTightBounds(featuresInViewport)
        : L.featureGroup(featuresInViewport).getBounds();
      
      if (bounds.isValid()) {
        const padding = isLevel ? [1, 1] : (isRoom ? [8, 8] : [3, 3]);
        ctrl.map.flyToBounds(bounds, { padding, duration: 0.2 }); // Padding is in pixels
        // After the fly animation completes, adjust zoom for context
        ctrl.map.once('moveend', () => {
          if (cls === 'Site' || cls === 'Building') {
            ctrl.map.setZoom(ctrl.map.getZoom() - 1);
          } else if (isLevel) {
            ctrl.map.setZoom(ctrl.map.getZoom() + 0.5);
          } else if (isRoom) {
            ctrl.map.setZoom(ctrl.map.getZoom() - 0.25);
          }
        });
        resetViewportRetries();
      } else {
        if (scheduleViewportRetry() && deferFallback) return;
        setFallbackView();
      }
    } else {
      if (scheduleViewportRetry() && deferFallback) return;
      setFallbackView();
    }
  };

  for (let plugin of PLUGINS) {
    new plugin(ctrl);
  }

  ctrl.eventBus.emit('mithril:oninit');
}; // End oninit

function oncreate (vnode) {
  let ctrl = this;
  ctrl.dom = vnode.dom;
  ctrl.eventBus.emit('mithril:beforecreate');
  ctrl.lastView = ctrl.mapSelectedView();
  let baseTiles = null;
  let mapboxGLLayer = null;
  const forceSvg = shouldForceSvgRenderer();
  const useMapboxGL = shouldUseMapboxGL();
  if (useMapboxGL && typeof L.mapboxGL === 'function') {
    try {
      mapboxGLLayer = L.mapboxGL({
        style: `mapbox://styles/${MAPBOX_STYLE_ID}`,
        accessToken: MAPBOX_ACCESS_TOKEN,
        maxNativeZoom: 15,
        maxZoom: 28,
        zoomOffset: -1,
      });
      baseTiles = mapboxGLLayer;
    } catch (error) {
      console.warn('[map] mapbox-gl failed to initialize, using raster tiles.', error);
    }
  } else if (useMapboxGL) {
    console.warn('[map] mapbox-gl-leaflet missing, using raster tiles.');
  }

  if (!baseTiles) {
    baseTiles = createMapboxRasterBaseLayer();
  }

  if (!baseTiles) {
    console.warn('[map] mapbox disabled or unavailable, using fallback tiles.');
    baseTiles = createFallbackBaseLayer();
  }

  const locationClass = ctrl.location()?.['@class'];
  const locationZoom = isRoomClass(locationClass)
    ? (DEFAULT_ZOOM_LEVELS.Room ?? 15)
    : (DEFAULT_ZOOM_LEVELS[locationClass] ?? 15);

  ctrl.map = new L.Map(vnode.dom, {
    layers: [baseTiles].filter(Boolean),
    center: new L.LatLng(50.02185841773444, 0.15380859375),
    zoom: ctrl.location().zoom ?? locationZoom,
    renderer: forceSvg && L.Browser && L.Browser.svg ? L.svg() : undefined,
    maxZoom: 28,
    zoomSnap: 0.5,
    scrollWheelZoom: false,
    attributionControl: false,
    zoomControl: false,
    dragging: false, 
    tap: false,
  });

  if (mapboxGLLayer) {
    watchMapboxGL(ctrl, mapboxGLLayer);
  }

    // --- Deterministic z-order panes ---
  ctrl.map.createPane('pane-locations');
  ctrl.map.getPane('pane-locations').style.zIndex = 400; // below equipment

  ctrl.map.createPane('pane-zones');
  ctrl.map.getPane('pane-zones').style.zIndex = 450; // above locations, below equipment

  ctrl.map.createPane('pane-equipment');
  ctrl.map.getPane('pane-equipment').style.zIndex = 500; // above locations

  ctrl.map.createPane('pane-labels');
  ctrl.map.getPane('pane-labels').style.zIndex = 600; // topmost for text/labels

  
  ctrl.map.getRenderer(ctrl.map).options.padding = 2;

  L.control.zoom({
    position: 'bottomright',
    zoomInText: '+',
    zoomOutText: '-',
    zoomInTitle: 'Zoom in',
    zoomOutTitle: 'Zoom out'
  }).addTo(ctrl.map);

  // Function to enable both scrollWheelZoom and dragging
  function enableInteractions() {
    ctrl.map.scrollWheelZoom.enable();
    ctrl.map.dragging.enable();
    ctrl.map.off('mousedown touchstart', enableInteractions);
  }

  // Function to disable both scrollWheelZoom and dragging
  function disableInteractions() {
    ctrl.map.scrollWheelZoom.disable();
    ctrl.map.dragging.disable();
    ctrl.map.on('mousedown touchstart', enableInteractions);
  }

  // Event listener for enabling interactions on map click or touchstart
  ctrl.map.on('mousedown touchstart', enableInteractions);

  // Optionally, disable interactions when the map loses focus
  ctrl.map.on('blur', disableInteractions);

  // Notify parent of fullscreen changes so it can hide competing UI
  const mapContainer = ctrl.map.getContainer();
  if (typeof ResizeObserver !== 'undefined') {
    ctrl._mapResizeObserver = new ResizeObserver(ctrl.scheduleMapResize);
    ctrl._mapResizeObserver.observe(mapContainer);
  }
  // Expose map reference on the container for tests/tooling
  try {
    mapContainer.__nrMap = ctrl.map;
  } catch (_) {}
  const fireFullscreenChange = () => {
    const isFull = mapContainer.classList.contains('leaflet-pseudo-fullscreen') ||
      mapContainer.classList.contains('leaflet-fullscreen-on');
    // Lock page scroll while fullscreen is active
    if (typeof document !== 'undefined' && document.body) {
      if (isFull) {
        if (ctrl._originalBodyOverflow === null) {
          ctrl._originalBodyOverflow = document.body.style.overflow;
        }
        document.body.style.overflow = 'hidden';
      } else if (ctrl._originalBodyOverflow !== null) {
        document.body.style.overflow = ctrl._originalBodyOverflow;
        ctrl._originalBodyOverflow = null;
      }
    }
    const cb = ctrl.onFullscreenChangeCb;
    if (cb) cb(isFull);
  };
  ctrl.map.on('fullscreenchange', fireFullscreenChange);
  fireFullscreenChange();

  let currentFeatures = ctrl.getCurrentFeatures();

  ctrl.lastLayerHash = currentFeatures.map( ctrl.getFeatureUid ).join('');
  ctrl.lastFeatureset = currentFeatures;

  ctrl.eventBus.emit('mithril:oncreate'); // Emit event to allow plugins to hook into the map creation and start listening to feature events
  ctrl.eventBus.emit('featuresToAdd', currentFeatures); // Emit event to add features to the map
  ctrl.map.invalidateSize();
  ctrl.setViewportAndAdjustBounds(ctrl);
  // Retry viewport fit shortly after initial load in case layers/panes settle asynchronously
  setTimeout(() => {
    if (ctrl?.map) ctrl.setViewportAndAdjustBounds(ctrl);
  }, ctrl._viewportRetryDelayMs);
}

function onupdate(vnode) {
  let ctrl = this;
  // Keep fullscreen callback in sync with latest attrs
  ctrl.onFullscreenChangeCb = typeof vnode.attrs.onFullscreenChange === 'function'
    ? vnode.attrs.onFullscreenChange
    : null;

  // Check if the features have changed
  let newLayerHash = ctrl.getCurrentFeatures().map( ctrl.getFeatureUid ).join('');
  if (ctrl.lastLayerHash === newLayerHash) return;
  ctrl.lastLayerHash = newLayerHash;

  ctrl.ancestors = () => ctrl.location().path;
  ctrl.childClass = LOCATION_CLASSES[LOCATION_CLASSES.indexOf(ctrl.location()['@class']) + 1] ?? 'Room';

  let newFeatureset = ctrl.getCurrentFeatures();
  // Create a Set of combined IDs from the current layerset for quick lookup
  let currentFeatureUIDs = new Set(ctrl.lastFeatureset.map( ctrl.getFeatureUid ));
  // Create a Set of combined IDs from the new featureset for quick lookup
  let newFeatureUIDs = new Set(newFeatureset.map( ctrl.getFeatureUid ));

  // Filter out features from the old featureset that are not in the new featureset
  let featuresToRemove = ctrl.lastFeatureset.filter(feature => !newFeatureUIDs.has(ctrl.getFeatureUid(feature)))
    .map(ctrl.getFeatureUid);
  // Filter out features from the newFeatureset that are not in the current featureset
  let featuresToAdd = newFeatureset.filter(feature => !currentFeatureUIDs.has(ctrl.getFeatureUid(feature)));
  // Update the lastFeatureset
  ctrl.lastFeatureset = newFeatureset;
  
  if (featuresToRemove.length > 0) ctrl.eventBus.emit('featuresToRemove', featuresToRemove);
  if (featuresToAdd.length > 0) ctrl.eventBus.emit('featuresToAdd', featuresToAdd);

  ctrl.map.invalidateSize();
  EVENT_BUS.emit('mithril:onupdate');
  ctrl.setViewportAndAdjustBounds(ctrl);
}

function onremove (vnode) {
  let ctrl = this;
  EVENT_BUS.emit('mithril:onremove');
  // Restore body scroll state if we changed it
  if (typeof document !== 'undefined' && document.body && ctrl._originalBodyOverflow !== null) {
    document.body.style.overflow = ctrl._originalBodyOverflow;
    ctrl._originalBodyOverflow = null;
  }
  if (ctrl._mapResizeObserver) {
    ctrl._mapResizeObserver.disconnect();
    ctrl._mapResizeObserver = null;
  }
  if (ctrl._mapResizeFrame && typeof window !== 'undefined') {
    window.cancelAnimationFrame(ctrl._mapResizeFrame);
    ctrl._mapResizeFrame = null;
  }
  if (ctrl._mapboxGLFallbackTimer) {
    clearTimeout(ctrl._mapboxGLFallbackTimer);
    ctrl._mapboxGLFallbackTimer = null;
  }
  ctrl.map.remove();
}

function view (vnode) {
  return m('div', {id: 'mapdiv', style: 'position: relative; height: 100%; width: 100%;'}, [
    // m('div', {id: 'mapoverlaydiv', style: ' position: absolute; top:0; left:0; height: 100%; width: 100%; z-index: 701; '}),
  ]);
};

module.exports = {oninit, oncreate, onupdate, onremove, view};
